/**
 * CampagneParcoursIllustration
 * --------------------------------------------------------------------
 * Illustration vectorielle codée en dur (SVG + Tailwind + framer-motion)
 * qui matérialise le parcours candidat dans une campagne recruteurs.io :
 *
 *   01. Audit marque employeur
 *   02. Diffusion de contenus
 *   03. Vivier qualifié
 *   04. Acculturation continue
 *   05. Activation Parcours Découverte → embauche
 *
 * Pas de dépendance image externe, pas de Canva — chaque étape est
 * un nœud animé relié par un chemin pointillé. Couleurs alignées sur
 * la charte (#d03484 → #d47254). Animation séquentielle au mount.
 */

import { motion } from 'framer-motion';
import {
  Compass,
  Megaphone,
  Users,
  BookOpen,
  Sparkles,
  Star,
} from 'lucide-react';

const steps = [
  { icon: Compass,   label: 'Audit marque employeur',    delay: 0.0 },
  { icon: Megaphone, label: 'Contenus diffusés',         delay: 0.15 },
  { icon: Users,     label: 'Vivier qualifié',           delay: 0.3 },
  { icon: BookOpen,  label: 'Acculturation',             delay: 0.45 },
  { icon: Sparkles,  label: 'Parcours Découverte',       delay: 0.6 },
] as const;

export default function CampagneParcoursIllustration() {
  return (
    <div className="relative w-full max-w-[520px] aspect-square mx-auto">
      {/* Halo radial discret en fond */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full bg-gradient-to-br from-[#d03484]/15 via-transparent to-[#d47254]/15 blur-3xl"
      />

      {/* Petits points décoratifs */}
      <DecorativeDots />

      {/* Carte centrale verticale : 5 étapes connectées */}
      <div className="relative z-10 flex h-full flex-col justify-between py-6">
        {steps.map((step, idx) => (
          <StepNode
            key={step.label}
            Icon={step.icon}
            label={step.label}
            delay={step.delay}
            index={idx}
            isLast={idx === steps.length - 1}
            position={idx % 2 === 0 ? 'left' : 'right'}
          />
        ))}
      </div>

      {/* Badge "Embauche solide" en sortie */}
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.85, duration: 0.5, ease: 'easeOut' }}
        className="absolute -bottom-2 right-2 md:right-6 z-20 flex items-center gap-2 rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254] px-3 py-1.5 text-xs font-bold whitespace-nowrap text-white shadow-xl shadow-[#d03484]/30"
      >
        <Star className="w-3.5 h-3.5 fill-current" />
        Embauche solide
      </motion.div>
    </div>
  );
}

// ── Sous-composants ─────────────────────────────────────────────────

type StepNodeProps = {
  Icon: typeof Compass;
  label: string;
  delay: number;
  index: number;
  isLast: boolean;
  position: 'left' | 'right';
};

function StepNode({ Icon, label, delay, index, isLast, position }: StepNodeProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: position === 'left' ? -24 : 24 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className={`relative flex items-center gap-4 ${
        position === 'right' ? 'flex-row-reverse self-end pr-4' : 'self-start pl-4'
      }`}
    >
      {/* Cercle avec icône */}
      <div className="relative flex-shrink-0">
        <motion.div
          animate={{
            boxShadow: [
              '0 8px 24px -6px rgba(208, 52, 132, 0.35)',
              '0 12px 32px -4px rgba(212, 114, 84, 0.5)',
              '0 8px 24px -6px rgba(208, 52, 132, 0.35)',
            ],
          }}
          transition={{ duration: 3, repeat: Infinity, delay: delay * 2 }}
          className="h-14 w-14 md:h-16 md:w-16 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center text-white"
        >
          <Icon className="w-6 h-6 md:w-7 md:h-7" strokeWidth={2.2} />
        </motion.div>
        {/* Numéro de l'étape */}
        <div className="absolute -top-2 -left-2 h-6 w-6 rounded-full bg-white ring-2 ring-[#d03484] flex items-center justify-center">
          <span className="text-[10px] font-bold text-[#d03484] tabular-nums">
            0{index + 1}
          </span>
        </div>
      </div>

      {/* Label */}
      <div
        className={`rounded-xl bg-white/90 backdrop-blur-sm ring-1 ring-neutral-200 shadow-sm px-3 py-2 ${
          position === 'right' ? 'text-right' : 'text-left'
        }`}
      >
        <p className="text-sm font-semibold text-neutral-900 leading-tight whitespace-nowrap">
          {label}
        </p>
      </div>

      {/* Connecteur pointillé vers l'étape suivante (sauf le dernier) */}
      {!isLast && (
        <ConnectorDots
          fromPosition={position}
          delay={delay + 0.3}
        />
      )}
    </motion.div>
  );
}

function ConnectorDots({
  fromPosition,
  delay,
}: {
  fromPosition: 'left' | 'right';
  delay: number;
}) {
  // Le connecteur descend en biais vers l'étape suivante (qui sera de l'autre côté)
  const isFromLeft = fromPosition === 'left';
  return (
    <motion.svg
      aria-hidden
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay, duration: 0.4 }}
      className={`absolute top-full ${
        isFromLeft ? 'left-12 md:left-14' : 'right-12 md:right-14'
      } w-32 h-12 -translate-y-1 pointer-events-none`}
      viewBox="0 0 120 48"
      fill="none"
    >
      <motion.path
        d={
          isFromLeft
            ? 'M 4 4 Q 60 4, 60 24 T 116 44'
            : 'M 116 4 Q 60 4, 60 24 T 4 44'
        }
        stroke="url(#brandGradient)"
        strokeWidth="2"
        strokeDasharray="3 5"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ delay, duration: 0.6, ease: 'easeInOut' }}
      />
      <defs>
        <linearGradient id="brandGradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#d03484" />
          <stop offset="100%" stopColor="#d47254" />
        </linearGradient>
      </defs>
    </motion.svg>
  );
}

function DecorativeDots() {
  // Quelques points décoratifs animés en arrière-plan, pour donner
  // de la profondeur sans encombrer le message principal.
  const dots = [
    { x: '8%',  y: '12%', size: 6,  delay: 0 },
    { x: '92%', y: '20%', size: 4,  delay: 0.5 },
    { x: '4%',  y: '85%', size: 5,  delay: 1 },
    { x: '88%', y: '78%', size: 7,  delay: 1.5 },
    { x: '50%', y: '4%',  size: 3,  delay: 0.8 },
  ];
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none">
      {dots.map((d, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-gradient-to-br from-[#d03484] to-[#d47254] opacity-50"
          style={{
            left: d.x,
            top: d.y,
            width: d.size,
            height: d.size,
          }}
          animate={{ y: [0, -8, 0], opacity: [0.5, 0.85, 0.5] }}
          transition={{
            duration: 4,
            repeat: Infinity,
            delay: d.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}
